import * as R from 'ramda';
import { ActionType } from '../../core/redux/actionType';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../core/api/module';
import { Endpoints } from '../../core/api/endpoints';
import { ReportDeliveryRequest, ReportDeliveryResponse } from '../models';
import { toMap } from '../../language/arrays';


const KEY_ACTION = ActionType.DeliverReportsRequest;
const ACTIONS    = [
  ActionType.DeliverReportsRequest,
  ActionType.DeliverReportsResponse,
  ActionType.DeliverReportsError,
];


type Payload = ReportDeliveryResponse;


type Meta = {
  deliveryRequest: ReportDeliveryRequest[];
  languageId: number;
};


/**
 * Dispatch this to retrieve all the participants whose raters the current user has been assigned to approve
 */
export const deliverReportsAction = (
  deliveryRequest: ReportDeliveryRequest[],
  languageId: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.DeliverReports,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify({
      reports: deliveryRequest,
      languageId: languageId,
    }),
    types:    makeRsaaTypes<Payload, Meta>(ACTIONS, { deliveryRequest, languageId }),
    bailout:  state => !!getLoading(state, KEY_ACTION),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const metaTag = `${action.meta.deliveryRequest}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  const allReports = toMap(
    state.reportRecipientReports || [],
    r => `${r.reportSetId}-${r.participantId}-${r.reportId}`,
  );

  const delivered = toMap(
    payload?.reports || [],
    r => `${r.reportSetId}-${r.participantId}-${r.reportId}`,
  ); 

  const merged = R.mergeRight(allReports, delivered);
  
  return R.assoc('reportRecipientReports', Object.values(merged), successState);
};

export const deliverReportsReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
